import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { placeMenu } from "./menuPlacement";

const SCROLL_GRACE_MS = 300;

// A menu at the pointer — what the right mouse button opens. The items are
// plain `.wl-menu__item` buttons with role="menuitem" (or menuitemcheckbox /
// menuitemradio, for a choice that is on or off), passed as children, so a
// screen builds its own list from the design system's menu parts. This only
// does what every context menu needs: where it goes, how it closes, and the
// keyboard.
//
// In document.body, so no scrolling list or full-screen layer can clip it.
export function ContextMenu({
  x,
  y,
  label,
  onClose,
  children,
}: {
  x: number;
  y: number;
  label: string;
  // `fromInside`: closed by Escape or by choosing an item, so focus goes back
  // to where the menu was opened. A click or scroll elsewhere leaves focus
  // wherever the user put it.
  onClose: (fromInside: boolean) => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const items = () =>
    Array.from(
      ref.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not(:disabled):not([aria-disabled="true"])') ?? [],
    );

  // Measured before the first paint, so the menu never shows itself running
  // off the window and then jumping back.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setPos(placeMenu({ x, y, width, height, viewportWidth: window.innerWidth, viewportHeight: window.innerHeight }));
    // Focus on the first item: the arrow keys work straight away, and a menu
    // opened with the keyboard is usable without a mouse. After a mouse click
    // the browser shows no focus ring for it, so nothing looks preselected.
    items()[0]?.focus({ preventScroll: true });
  }, [x, y]);

  useEffect(() => {
    const outside = (target: EventTarget | null) => !(target instanceof Node && ref.current?.contains(target));
    const onPointerDown = (e: PointerEvent) => {
      if (outside(e.target)) closeRef.current(false);
    };
    // The list scrolling away under a fixed menu would leave it pointing at
    // the wrong row. Not in the first moments, though: a wheel scroll glides on
    // for a little while, and a right-click straight after it would otherwise
    // open a menu that closes before it is seen.
    const openedAt = performance.now();
    const onScroll = (e: Event) => {
      if (performance.now() - openedAt < SCROLL_GRACE_MS) return;
      if (outside(e.target)) closeRef.current(false);
    };
    // Also covers a click into the mail body: that is another document, whose
    // pointer events never reach this one, but it does take the focus away.
    const onBlur = () => closeRef.current(false);
    const onResize = () => closeRef.current(false);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("scroll", onScroll, true);
    window.addEventListener("blur", onBlur);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  function onKeyDown(e: React.KeyboardEvent) {
    // No key goes past an open menu: the screen underneath has single-letter
    // shortcuts, and E or Delete there would act on a mail while you are only
    // choosing from the menu.
    e.stopPropagation();
    const list = items();
    const i = list.indexOf(document.activeElement as HTMLElement);
    const focus = (n: number) => list[(n + list.length) % list.length]?.focus();
    if (e.key === "ArrowDown") {
      e.preventDefault();
      focus(i + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      focus(i < 0 ? -1 : i - 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      focus(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focus(-1);
    } else if (e.key === "Enter" || e.key === " ") {
      // Chosen here rather than left to the button's own activation, which
      // hangs on a keypress that not every input path produces.
      e.preventDefault();
      if (i >= 0) list[i]!.click();
    } else if (e.key === "Escape" || e.key === "Tab") {
      e.preventDefault();
      onClose(true);
    }
  }

  return createPortal(
    <div
      ref={ref}
      className="wl-popover"
      role="menu"
      aria-label={label}
      onKeyDown={onKeyDown}
      // Choosing an item closes the menu; the item's own click has run by then.
      // A greyed item leaves it open: it did nothing, so there is nothing to see.
      onClick={(e) => {
        e.stopPropagation();
        if ((e.target as HTMLElement).closest('[role^="menuitem"]:not([aria-disabled="true"])')) onClose(true);
      }}
      onContextMenu={(e) => e.preventDefault()}
      style={{ position: "fixed", left: pos.left, top: pos.top, minWidth: "220px", maxWidth: "320px" }}
    >
      <div className="wl-menu">{children}</div>
    </div>,
    document.body,
  );
}
